import React, { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useCollectionBinding, useSlotGroup, useGallery, RichTextContent } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
// Hooks and GlowTrace live in the parent theme and aren't re-exported from
// its package entry, so we keep local copies under src/ for functional parity.
// Keep those files in sync with @vibesites/theme-preferred/src/ when the base
// theme changes.
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { isNonMarketingGraphic, isStagedPhoto } from '../utils/sortGallery';
import { GlowTrace } from './GlowTrace';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  subtitle: string;
  param: string;
  count: number;
  priceRange: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

function keyToShortLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bed',
    two: '2 Bed',
    three: '3 Bed',
    four: '4 Bed',
    five: '5 Bed',
  };
  return labels[key] || `${key} Bed`;
}

/**
 * Parses a single bedroom-range endpoint into a numeric value.
 * "studio"/"std"/"0" -> 0, otherwise the parsed integer. Returns null
 * when the endpoint can't be understood as a number.
 */
function parseBedroomEndpoint(part: string): number | null {
  const lower = part.trim().toLowerCase();
  if (!lower) return null;
  if (lower === 'studio' || lower === 'std') return 0;
  const num = parseInt(lower, 10);
  return isNaN(num) ? null : num;
}

/** Renders a numeric bedroom value: 0 -> "Studio", otherwise the number. */
function bedroomValueToLabel(value: number): string {
  return value === 0 ? 'Studio' : String(value);
}

/** Joins a list with commas and an Oxford "and" (e.g. "Studio, 1, and 2"). */
function joinWithAnd(items: string[]): string {
  if (items.length <= 1) return items.join('');
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/**
 * Formats a raw bedrooms range into natural copy that enumerates every
 * value in the range rather than showing just the endpoints — e.g.
 * "studio - 2" -> "Studio, 1, and 2 Bedroom Layouts", "1 - 3" ->
 * "1, 2, and 3 Bedroom Layouts". Drops "Bedroom" when the result is only
 * studios, and falls back to the raw endpoints when they can't be parsed.
 */
function formatBedroomsLayoutsLabel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Split on any dash variant (hyphen, en-dash, em-dash) so ranges using
  // typographic dashes (e.g. "3 – 3") still parse correctly.
  const parts = trimmed.split(/\s*[-–—]\s*/).filter((p) => p.trim());
  const endpoints = parts.map(parseBedroomEndpoint);

  // If any endpoint is unparseable, fall back to the normalized raw copy
  // (studio-cased endpoints joined by a dash) rather than guessing.
  if (!endpoints.length || endpoints.some((e) => e === null)) {
    const normalized = parts
      .map((p) => (parseBedroomEndpoint(p) === 0 ? 'Studio' : p.trim()))
      .join(' - ');
    return normalized ? `${normalized} Bedroom Layouts` : null;
  }

  const nums = endpoints as number[];
  const min = Math.min(...nums);
  const max = Math.max(...nums);

  // Enumerate every value from min to max inclusive.
  const values: number[] = [];
  for (let v = min; v <= max; v++) values.push(v);

  const list = joinWithAnd(values.map(bedroomValueToLabel));
  const allStudios = values.every((v) => v === 0);
  return allStudios ? `${list} Layouts` : `${list} Bedroom Layouts`;
}

function formatPriceRange(plans: Floorplan[]): string {
  const prices = plans
    .flatMap((p) => [p.floorplan_price_min, p.floorplan_price_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!prices.length) return 'Call for Pricing';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `From $${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const plans = groups[key];
    const count = plans.reduce((sum, p) => {
      const units = p.floorplan_available_units ? parseInt(String(p.floorplan_available_units), 10) : 0;
      return sum + (isNaN(units) ? 0 : units);
    }, 0);
    return {
      key,
      label: keyToLabel(key),
      subtitle: keyToShortLabel(key),
      param: key,
      count,
      priceRange: formatPriceRange(plans),
    };
  });
}

// ============================================================================
// Interior photo selection from gallery
// ============================================================================

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'unit', 'bedroom', 'living room', 'living',
  'kitchen', 'bathroom', 'bath', 'inside', 'home', 'den', 'dining',
  'closet', 'counter', 'cabinet', 'window', 'carpet',
  'hardwood', 'granite', 'stainless', 'appliance', 'washer', 'dryer',
];

function scoreInterior(photo: PhotoGallery): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  let score = INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
  // Strongly prefer virtually staged / furnished / model-unit photography.
  if (isStagedPhoto(photo)) score += 50;
  return score;
}

/**
 * Strips query params and trailing slashes so CDN variants of
 * the same image (e.g. ?width=400 vs ?width=800) are treated as one.
 */
function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

/**
 * Spread picks across a scored pool so adjacent cards don't share near-
 * identical room angles (e.g. three kitchens in a row).
 */
function spreadPick(pool: { photo: PhotoGallery; score: number }[], count: number): string[] {
  if (pool.length <= count) {
    return pool.map((p) => p.photo.image!);
  }
  const step = pool.length / count;
  const picked: string[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.min(Math.floor(i * step), pool.length - 1);
    picked.push(pool[idx].photo.image!);
  }
  return picked;
}

function pickInteriorPhotos(gallery: PhotoGallery[], count: number): string[] {
  if (!gallery.length || count <= 0) return [];

  // Deduplicate, drop logos / floor-plan drawings, then score interiors.
  const seen = new Set<string>();
  const unique: { photo: PhotoGallery; score: number }[] = [];
  for (const photo of gallery) {
    if (!photo.image) continue;
    if (isNonMarketingGraphic(photo)) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push({ photo, score: scoreInterior(photo) });
  }

  unique.sort((a, b) => b.score - a.score);

  // Prefer staged photography when available; fill remaining slots from
  // other interior shots so every bedroom card still has an image.
  const staged = unique.filter((item) => isStagedPhoto(item.photo));
  if (staged.length > 0) {
    const stagedPicks = spreadPick(staged, Math.min(count, staged.length));
    if (stagedPicks.length >= count) return stagedPicks;

    const stagedUrls = new Set(stagedPicks);
    const rest = unique.filter((item) => !stagedUrls.has(item.photo.image!));
    const restPool = rest.slice(0, Math.max((count - stagedPicks.length) * 2, 4));
    return [...stagedPicks, ...spreadPick(restPool, count - stagedPicks.length)];
  }

  const pool = unique.slice(0, Math.max(count * 2, 8));
  return spreadPick(pool, count);
}

// ============================================================================
// Card slot group definitions
// ============================================================================
// Each floor plan card has an editable image, heading, and sub_label slot so
// users can override the auto-selected gallery photo and overlay text via the
// content editor. We support up to 7 cards (1 "View All" + 6 bedroom categories).

function makeCardDef(
  slotLabel: string,
  headingDefault: string,
  subLabelDefault: string,
): SlotGroupDefinition {
  return {
    label: slotLabel,
    fields: {
      image: {
        label: 'Card Image',
        type: 'image',
        defaultValue: '',
      },
      heading: {
        label: 'Card Heading',
        type: 'richtext',
        defaultValue: headingDefault,
      },
      sub_label: {
        label: 'Card Sub Label',
        type: 'richtext',
        defaultValue: subLabelDefault,
      },
    },
  };
}

const cardDefs = {
  viewAll: makeCardDef('View All Card', '<p>ALL</p>', '<p>View All Layouts</p>'),
  card0: makeCardDef('Card 1', '', ''),
  card1: makeCardDef('Card 2', '', ''),
  card2: makeCardDef('Card 3', '', ''),
  card3: makeCardDef('Card 4', '', ''),
  card4: makeCardDef('Card 5', '', ''),
  card5: makeCardDef('Card 6', '', ''),
};

// ============================================================================
// Category Card Inner
// ============================================================================

function CategoryCardInner({
  cat,
  image,
  delay,
  headingHtml,
  subLabelHtml,
}: {
  cat: BedroomCategory;
  image: string;
  delay: number;
  headingHtml?: string | null;
  subLabelHtml?: string | null;
}) {
  const computedHeading = cat.key === 'studio' ? 'S' : BED_ORDER.indexOf(cat.key).toString();
  const computedSubLabel = `${cat.label} Layouts`;

  return (
    <a
      data-reveal
      href={`/floor-plans?bedroom=${cat.param}`}
      className="reveal-fade-up fp-card glow-trace-wrap group relative block aspect-[4/3] sm:aspect-[3/4] overflow-hidden cursor-pointer"
    >
      <GlowTrace delay={delay} />
      {image && (
        <VibesitesImage
          url={image}
          alt={`${cat.label} floor plans`}
          className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
        />
      )}
      <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-[var(--vibe-color-text-on-primary)] px-4">
        {headingHtml ? (
          <div
            className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg"
            dangerouslySetInnerHTML={{ __html: headingHtml }}
          />
        ) : (
          <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">
            {computedHeading}
          </span>
        )}
        {subLabelHtml ? (
          <div
            className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md"
            dangerouslySetInnerHTML={{ __html: subLabelHtml }}
          />
        ) : (
          <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">
            {computedSubLabel}
          </span>
        )}
      </div>
    </a>
  );
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const label = useContentToken('floorplanslist.label');

  const formattedLabel = formatBedroomsLayoutsLabel(label);

  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const gallery = useGallery();

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  // +1 for the "View All" card
  const photos = useMemo(
    () => pickInteriorPhotos(gallery, categories.length + 1),
    [gallery, categories.length],
  );

  // Resolve card slot groups (hooks must be called unconditionally)
  const viewAllSlot = useSlotGroup('floorplanslist.card_viewall', cardDefs.viewAll);
  const cardSlot0 = useSlotGroup('floorplanslist.card_0', cardDefs.card0);
  const cardSlot1 = useSlotGroup('floorplanslist.card_1', cardDefs.card1);
  const cardSlot2 = useSlotGroup('floorplanslist.card_2', cardDefs.card2);
  const cardSlot3 = useSlotGroup('floorplanslist.card_3', cardDefs.card3);
  const cardSlot4 = useSlotGroup('floorplanslist.card_4', cardDefs.card4);
  const cardSlot5 = useSlotGroup('floorplanslist.card_5', cardDefs.card5);
  const cardSlots = [cardSlot0, cardSlot1, cardSlot2, cardSlot3, cardSlot4, cardSlot5];

  // Use overridden image if set, otherwise fall back to auto-picked gallery photo
  const viewAllImage = viewAllSlot?.image || photos[0] || '';
  const cardImages = categories.map((_, i) => cardSlots[i]?.image || photos[i + 1] || '');

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const hasCategories = categories.length > 0;
  const totalCards = categories.length + 1;

  const gridCols = useMemo(() => {
    if (totalCards <= 2) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2';
    if (totalCards === 3) return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3';
    if (totalCards === 4) return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4';
    if (totalCards === 5) return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5';
    return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6';
  }, [totalCards]);

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-primary)]">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div ref={headingRef as React.RefObject<HTMLDivElement>} className="reveal-fade-up text-center mb-12">
          {formattedLabel && (
            <EditableToken path="floorplanslist.label" defaultField="property_bedrooms_range" as="p" className="font-body text-base sm:text-lg text-[var(--vibe-color-text-on-primary)] mb-3">
              {formattedLabel}
            </EditableToken>
          )}
          <RichTextContent path="floorplanslist.title" label="Title" defaultValue="<p>Make Yourself at Home</p>" as="h2" className="font-heading text-3xl sm:text-5xl font-bold text-[var(--vibe-color-text-on-primary)] mb-4 tracking-tight" />
          <RichTextContent path="floorplanslist.subtitle" label="Subtitle" defaultValue="<p>Please select your desired layout below.</p>" as="p" className="font-body text-base sm:text-lg text-[var(--vibe-color-text-on-primary)] max-w-2xl mx-auto" />
        </div>

        {hasCategories ? (
          <div ref={gridRef as React.RefObject<HTMLDivElement>} className={`grid ${gridCols} gap-3 md:gap-4`}>
            {/* "View All" card first — editable image + richtext heading/sub_label */}
            <EditableSlotGroup groupPath="floorplanslist.card_viewall" definition={cardDefs.viewAll}>
              <a
                data-reveal
                href="/floor-plans"
                className="reveal-fade-up fp-card glow-trace-wrap group relative block aspect-[4/3] sm:aspect-[3/4] overflow-hidden cursor-pointer"
              >
                <GlowTrace delay={0.3} />
                {viewAllImage && (
                  <VibesitesImage
                    url={viewAllImage}
                    alt="View all floor plans"
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                )}
                <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-[var(--vibe-color-text-on-primary)] px-4">
                  <div
                    className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg"
                    dangerouslySetInnerHTML={{ __html: viewAllSlot?.heading || '<p>ALL</p>' }}
                  />
                  <div
                    className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md"
                    dangerouslySetInnerHTML={{ __html: viewAllSlot?.sub_label || '<p>View All Layouts</p>' }}
                  />
                </div>
              </a>
            </EditableSlotGroup>

            {/* Bedroom category cards — each with editable image + richtext overlay (explicit paths for linter) */}
            {categories[0] && <EditableSlotGroup groupPath="floorplanslist.card_0" definition={cardDefs.card0}><CategoryCardInner cat={categories[0]} image={cardImages[0]} delay={0.55} headingHtml={cardSlots[0]?.heading || null} subLabelHtml={cardSlots[0]?.sub_label || null} /></EditableSlotGroup>}
            {categories[1] && <EditableSlotGroup groupPath="floorplanslist.card_1" definition={cardDefs.card1}><CategoryCardInner cat={categories[1]} image={cardImages[1]} delay={0.80} headingHtml={cardSlots[1]?.heading || null} subLabelHtml={cardSlots[1]?.sub_label || null} /></EditableSlotGroup>}
            {categories[2] && <EditableSlotGroup groupPath="floorplanslist.card_2" definition={cardDefs.card2}><CategoryCardInner cat={categories[2]} image={cardImages[2]} delay={1.05} headingHtml={cardSlots[2]?.heading || null} subLabelHtml={cardSlots[2]?.sub_label || null} /></EditableSlotGroup>}
            {categories[3] && <EditableSlotGroup groupPath="floorplanslist.card_3" definition={cardDefs.card3}><CategoryCardInner cat={categories[3]} image={cardImages[3]} delay={1.30} headingHtml={cardSlots[3]?.heading || null} subLabelHtml={cardSlots[3]?.sub_label || null} /></EditableSlotGroup>}
            {categories[4] && <EditableSlotGroup groupPath="floorplanslist.card_4" definition={cardDefs.card4}><CategoryCardInner cat={categories[4]} image={cardImages[4]} delay={1.55} headingHtml={cardSlots[4]?.heading || null} subLabelHtml={cardSlots[4]?.sub_label || null} /></EditableSlotGroup>}
            {categories[5] && <EditableSlotGroup groupPath="floorplanslist.card_5" definition={cardDefs.card5}><CategoryCardInner cat={categories[5]} image={cardImages[5]} delay={1.80} headingHtml={cardSlots[5]?.heading || null} subLabelHtml={cardSlots[5]?.sub_label || null} /></EditableSlotGroup>}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-on-primary)]/60">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .fp-card {
          border-radius: 4px;
        }
        .fp-card-overlay {
          background: linear-gradient(
            180deg,
            color-mix(in srgb, var(--vibe-color-primary) 70%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 85%, black) 100%
          );
          opacity: 0.3;
        }
        .fp-card:hover .fp-card-overlay {
          opacity: 0.45;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.label': {
      label: 'Section Label',
      type: 'text',
      defaultField: 'property_bedrooms_range',
      description: 'Label above the title showing bedroom range (e.g., "1 - 3 Bedroom Layouts").',
      suggestedFields: ['property_bedrooms_range'],
    },
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Make Yourself at Home</p>',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Please select your desired layout below.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.card_viewall': {
      label: 'View All Card',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '<p>ALL</p>' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '<p>View All Layouts</p>' },
      },
    },
    'floorplanslist.card_0': {
      label: 'Card 1',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
    'floorplanslist.card_1': {
      label: 'Card 2',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
    'floorplanslist.card_2': {
      label: 'Card 3',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
    'floorplanslist.card_3': {
      label: 'Card 4',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
    'floorplanslist.card_4': {
      label: 'Card 5',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
    'floorplanslist.card_5': {
      label: 'Card 6',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
